// src/types/DataTypes.ts

// Import types from Italian data types
import { ItalianProvince, BESIndicatorMetadata } from './ItalianDataTypes';

/**
 * BES Categories for Italian well-being indicators
 */
export enum BESCategory {
  AMBIENTE = 'Environment',
  BENESSERE_ECONOMICO = 'Economic Well-being',
  SALUTE = 'Health',
  INNOVAZIONE = 'Innovation',
  ISTRUZIONE = 'Education and Training',
  LAVORO = 'Work',
  PAESAGGIO = 'Landscape and Cultural Heritage',
  POLITICA = 'Politics and Institutions',
  SERVIZI = 'Services',
  URBANO = 'Urban Quality'
}

/**
 * Main data record interface for Italian provinces with BES indicators
 */
export interface DataRecord {
  // Core geographic identifiers
  province_code: number;        // Italian province code (1-107)
  province_name: string;        // Province name (e.g., "Torino", "Milano")
  province_sigla: string;       // Province abbreviation (e.g., "TO", "MI")
  nuts3_code: string;           // NUTS3 code (e.g., "ITC11", "ITC44")
  year: number;                 // Year of the data point

  // Municipality identifiers
  PRO_COM?: string;             // 6-digit municipality code
  municipality_name?: string;   // Optional municipality name

  // Dynamic indicators
  [key: string]: any;           // Allow any indicator properties

  // Legacy compatibility fields
  iso_r?: string;               // Legacy: ISO code (now uses nuts3_code)
  country?: string;             // Country name (defaults to "Italy")
}

/**
 * Legacy European data record interface (preserved for migration purposes)
 * @deprecated Use ItalianDataRecord instead
 */
export interface EuropeanDataRecord {
    iso_r: string;                // Region ISO code
    year: number;                 // Year of the data point
    region: string;               // Region name
    pop: number;                  // Population
    country: string;              // Country name  
    c_wind: number;               // Wind energy consumption
    c_tech: number;               // Technology investment
    c_sulftax: number;            // Sulfur tax
    c_sulflim: number;            // Sulfur limitation
    c_sox: number;                // SOx emissions
    c_solar: number;              // Solar energy consumption
    c_renets: number;             // Renewable energy target scheme
    c_pmlim: number;              // Particulate matter limitation
    c_noxtax: number;             // NOx tax
    c_noxlim: number;             // NOx limitation
    c_nonmark: number;            // Non-market environmental policy
    c_market: number;             // Market-based environmental policy
    c_lowrd: number;              // Low-carbon R&D
    c_envpol: number;             // Environmental policy
    c_diesel: number;             // Diesel policy
    c_co2tax: number;             // CO2 tax
    c_co2ets: number;             // CO2 emissions trading scheme
    iso_c: string;                // Country ISO code
    pct: number;                  // PCT patent applications
    cd: number;                   // Carbon dioxide emissions
    gerd_edu: number;             // R&D expenditure in education
    gerd_gov: number;             // R&D expenditure in government
    gerd_biz: number;             // R&D expenditure in business
    rd_edu: number;               // R&D personnel in education
    rd_gov: number;               // R&D personnel in government
    rd_biz: number;               // R&D personnel in business
    pub10: number;                // Publications in top 10%
    copub: number;                // Co-publications
    pm25: number;                 // PM2.5 concentration
    gdp: number;                  // Gross domestic product
    median: number;               // Median income
    lllearn: number;              // Lifelong learning
    htec: number;                 // High-tech exports
    epo: number;                  // EPO patent applications
    egap: number;                 // Employment gap
    disp: number;                 // Income dispersion
    nacef: number;                // NACE F (Construction)
    nacebe: number;               // NACE B-E (Industry)
    nacea: number;                // NACE A (Agriculture)
    ted: number;                  // Total emissions data
    group: number;                // Group classification
    pim_epo: number;              // EPO patent impact measure
    pim_pct: number;              // PCT patent impact measure
    ch4: number;                  // Methane emissions
    co2: number;                  // Carbon dioxide emissions
    n2o: number;                  // Nitrous oxide emissions
    fgas: number;                 // Fluorinated gases emissions
    unemp: number;                // Unemployment rate
    pat_count: number;            // Patent count
    ecopat_count: number;         // Eco-patent count
  
    // Allow for future additions to the dataset
    [key: string]: string | number;
  }
  
/**
 * Variable metadata for display and formatting
 * Now uses BES indicator metadata structure
 */
export interface VariableMetadata {
  // Core fields
  id?: string;                   // Variable ID matching DataRecord keys
  code?: string;                 // Indicator code (e.g., "i10AMB003")
  label?: string;                // Human-readable label
  description?: string;          // Detailed description
  category?: VariableCategory;   // Category for grouping
  unit?: string;                 // Unit of measurement
  aggregatable?: boolean;        // Whether this variable can be aggregated
  
  // BES indicator fields
  name_en?: string;             // English name
  name_it?: string;             // Italian name
  description_en?: string;      // English description
  description_it?: string;      // Italian description
  subcategory?: string;         // Subcategory
  domain_color?: string;        // Color for the domain
  format?: string;              // Display format (e.g., '.1f', '.0f')
  colorScale?: string;          // Default color scale
  invertScale?: boolean;        // Whether higher values are negative
  visible?: boolean;            // Show in selectors by default
  gender_disaggregated?: boolean; // Has M/F/T variants
  yearly_data?: boolean;        // Has time series data
  min_value?: number;           // Minimum value in dataset
  max_value?: number;           // Maximum value in dataset
  avg_value?: number;           // Average value in dataset
  domain?: string;              // Domain key from metadata (e.g., climate, geo)
  order?: number;               // Suggested ordering within domain
  sourceFile?: string;          // Originating source file identifier
  timeInvariant?: boolean;      // True for non-temporal indicators
}

/**
 * Categories for grouping variables
 * Now extends BES categories
 */
export enum VariableCategory {
  // BES Categories (mapped from BESCategory)
  AMBIENTE = 'Environment',
  BENESSERE_ECONOMICO = 'Economic Well-being',
  INNOVAZIONE = 'Innovation',
  ISTRUZIONE = 'Education and Training',
  LAVORO = 'Work',
  PAESAGGIO = 'Landscape and Cultural Heritage',
  POLITICA = 'Politics and Institutions',
  SALUTE = 'Health',
  SERVIZI = 'Services',
  
  // Legacy categories (for backward compatibility)
  DEMOGRAPHIC = 'Demographic',
  ECONOMIC = 'Economic',
  ENVIRONMENTAL = 'Environmental',
  POLICY = 'Policy',
  ENERGY = 'Energy',
  OTHER = 'Other'
}
  
  /**
   * Data aggregation methods
   */
  export enum AggregationMethod {
    SUM = 'sum',
    AVERAGE = 'average',
    MEDIAN = 'median',
    MIN = 'min',
    MAX = 'max',
    COUNT = 'count'
  }
  
  /**
   * Time period for data analysis
   */
  export interface TimePeriod {
    startYear: number;
    endYear: number;
  }
  
/**
 * Region/Province data with geospatial properties
 * Now uses Italian province structure
 */
export interface Region extends ItalianProvince {
  // Legacy compatibility fields
  iso?: string;               // Legacy: ISO code (now uses nuts3_code)
  country?: string;           // Legacy: Country (now always "Italy")
}

// Type aliases for backward compatibility
export type ItalianDataRecord = DataRecord;
